import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export default function Step4ClinicalInfo({ formData, updateFormData }) {
  const handleClinicalChange = (field, value) => {
    updateFormData({
      ...formData,
      clinical_data: {
        ...formData.clinical_data,
        [field]: value,
      },
    });
  };

  const handleCultivoChange = (section, values) => {
    updateFormData({
      ...formData,
      cultivo_data: {
        ...formData.cultivo_data,
        [section]: values,
      },
    });
  };

  const toggleCheckbox = (section, value) => {
    const current = formData.cultivo_data[section] || [];
    const newValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    handleCultivoChange(section, newValues);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
          Información Clínica Adicional
        </h2>
        <p className="text-slate-600">
          Datos médicos relevantes para el análisis
        </p>
      </div>

      {/* Datos Generales */}
      <div className="bg-slate-50 p-6 rounded-lg space-y-4 border border-slate-200">
        <h3 className="font-heading font-semibold text-lg text-slate-900">
          Datos Generales
        </h3>

        <div className="space-y-3">
          <Label className="font-medium text-slate-700">
            ¿Toma antibióticos actualmente?
          </Label>
          <RadioGroup
            value={formData.clinical_data.toma_antibioticos.toString()}
            onValueChange={(value) =>
              handleClinicalChange('toma_antibioticos', value === 'true')
            }
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="antibioticos-si" data-testid="antibioticos-si" />
              <Label htmlFor="antibioticos-si" className="cursor-pointer">Sí</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="antibioticos-no" data-testid="antibioticos-no" />
              <Label htmlFor="antibioticos-no" className="cursor-pointer">No</Label>
            </div>
          </RadioGroup>
        </div>

        {formData.clinical_data.toma_antibioticos && (
          <div className="space-y-2">
            <Label htmlFor="fecha_ultima_dosis" className="font-medium text-slate-700">
              Fecha de última dosis
            </Label>
            <Input
              id="fecha_ultima_dosis"
              data-testid="fecha-ultima-dosis-input"
              type="date"
              value={formData.clinical_data.fecha_ultima_dosis || ''}
              onChange={(e) =>
                handleClinicalChange('fecha_ultima_dosis', e.target.value)
              }
              className="h-11"
            />
          </div>
        )}

        <div className="space-y-3">
          <Label className="font-medium text-slate-700">
            ¿Alérgico a antibióticos?
          </Label>
          <RadioGroup
            value={formData.clinical_data.alergico_antibioticos.toString()}
            onValueChange={(value) =>
              handleClinicalChange('alergico_antibioticos', value === 'true')
            }
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="alergico-si" data-testid="alergico-si" />
              <Label htmlFor="alergico-si" className="cursor-pointer">Sí</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="alergico-no" data-testid="alergico-no" />
              <Label htmlFor="alergico-no" className="cursor-pointer">No</Label>
            </div>
          </RadioGroup>
        </div>

        {formData.clinical_data.alergico_antibioticos && (
          <div className="space-y-2">
            <Label htmlFor="cual_antibiotico" className="font-medium text-slate-700">
              ¿Cuál?
            </Label>
            <Input
              id="cual_antibiotico"
              data-testid="cual-antibiotico-input"
              placeholder="Especifique el antibiótico"
              value={formData.clinical_data.cual_antibiotico || ''}
              onChange={(e) =>
                handleClinicalChange('cual_antibiotico', e.target.value)
              }
              className="h-11"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="tiempo_evolucion" className="font-medium text-slate-700">
            Tiempo de Evolución
          </Label>
          <Input
            id="tiempo_evolucion"
            data-testid="tiempo-evolucion-input"
            placeholder="Ej: 3 días, 2 semanas..."
            value={formData.clinical_data.tiempo_evolucion || ''}
            onChange={(e) =>
              handleClinicalChange('tiempo_evolucion', e.target.value)
            }
            className="h-11"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="origen_toma_muestra" className="font-medium text-slate-700">
            Origen de la toma de muestra
          </Label>
          <Input
            id="origen_toma_muestra"
            data-testid="origen-muestra-input"
            placeholder="Ej: Sangre venosa, orina, etc."
            value={formData.clinical_data.origen_toma_muestra || ''}
            onChange={(e) =>
              handleClinicalChange('origen_toma_muestra', e.target.value)
            }
            className="h-11"
          />
        </div>
      </div>

      {/* Secciones de Cultivo - Colapsables */}
      <div className="bg-white rounded-lg border border-slate-200">
        <h3 className="font-heading font-semibold text-lg text-slate-900 p-4 border-b border-slate-200">
          Secciones de Cultivo y Microscopía
        </h3>
        <Accordion type="multiple" className="w-full">
          {/* Cultivo Faríngea/Nasofaríngea */}
          <AccordionItem value="faringea">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-heading font-medium text-slate-900">
                Cultivo-Microscopía Faríngea/Nasofaríngea
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 checkbox-group pt-2">
                {['Ulceraciones', 'Manchas blanquecinas', 'Tos', 'Amígdalas inflamadas', 'Región amigdalina y faríngea normales'].map((item) => (
                  <label key={item} className="flex items-center gap-2">
                    <Checkbox
                      data-testid={`faringea-${item.toLowerCase().replace(/\s/g, '-')}`}
                      checked={(formData.cultivo_data.faringea || []).includes(item)}
                      onCheckedChange={() => toggleCheckbox('faringea', item)}
                    />
                    <span className="text-sm text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Cultivo Exudado Diversos */}
          <AccordionItem value="exudado">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-heading font-medium text-slate-900">
                Cultivo-Microscopía Exudado Diversos/Líquidos Corporales/Lesiones
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 checkbox-group pt-2">
                {['Exudado purulento', 'Exudado sanguinolento', 'Dolor a la manipulación', 'Fiebre documentada (MVZ)'].map((item) => (
                  <label key={item} className="flex items-center gap-2">
                    <Checkbox
                      data-testid={`exudado-${item.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}`}
                      checked={(formData.cultivo_data.exudado_diversos || []).includes(item)}
                      onCheckedChange={() => toggleCheckbox('exudado_diversos', item)}
                    />
                    <span className="text-sm text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Cultivo Nasal */}
          <AccordionItem value="nasal">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-heading font-medium text-slate-900">
                Cultivo-Microscopía Nasal
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 checkbox-group pt-2">
                {['Estornudos', 'Secreción o congestión nasal', 'Cuadro respiratorio recurrente', 'Fosas nasales sin alteraciones visibles'].map((item) => (
                  <label key={item} className="flex items-center gap-2">
                    <Checkbox
                      data-testid={`nasal-${item.toLowerCase().replace(/\s/g, '-')}`}
                      checked={(formData.cultivo_data.nasal || []).includes(item)}
                      onCheckedChange={() => toggleCheckbox('nasal', item)}
                    />
                    <span className="text-sm text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Urocultivo */}
          <AccordionItem value="urocultivo">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-heading font-medium text-slate-900">
                Urocultivo-Microscopía de Sedimento Urinario
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 space-y-4">
              <div className="space-y-3 pt-2">
                <Label className="font-medium text-slate-700">
                  ¿Antecedente de infección urinaria diagnosticada por MVZ?
                </Label>
                <RadioGroup
                  value={(formData.cultivo_data.urocultivo?.antecedente_infeccion || false).toString()}
                  onValueChange={(value) =>
                    handleCultivoChange('urocultivo', {
                      ...formData.cultivo_data.urocultivo,
                      antecedente_infeccion: value === 'true'
                    })
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="urocultivo-antecedente-si" data-testid="urocultivo-antecedente-si" />
                    <Label htmlFor="urocultivo-antecedente-si" className="cursor-pointer">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="urocultivo-antecedente-no" data-testid="urocultivo-antecedente-no" />
                    <Label htmlFor="urocultivo-antecedente-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label className="font-medium text-slate-700">
                  ¿Ha notado olor fuerte o fétido en la orina?
                </Label>
                <RadioGroup
                  value={(formData.cultivo_data.urocultivo?.olor_fuerte || false).toString()}
                  onValueChange={(value) =>
                    handleCultivoChange('urocultivo', {
                      ...formData.cultivo_data.urocultivo,
                      olor_fuerte: value === 'true'
                    })
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="urocultivo-olor-si" data-testid="urocultivo-olor-si" />
                    <Label htmlFor="urocultivo-olor-si" className="cursor-pointer">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="urocultivo-olor-no" data-testid="urocultivo-olor-no" />
                    <Label htmlFor="urocultivo-olor-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 checkbox-group">
                {[
                  'Signos compatibles con dolor abdominal o lumbar',
                  'Dolor abdominal a la palpación (MVZ)',
                  'Esfuerzo o dolor aparente al orinar',
                  'Micciones frecuentes con poco volumen',
                  'Secreción uretral o vaginal',
                  'Orina turbia o sangre visible en orina'
                ].map((item) => (
                  <label key={item} className="flex items-center gap-2">
                    <Checkbox
                      data-testid={`urocultivo-${item.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}`}
                      checked={(formData.cultivo_data.urocultivo?.sintomas || []).includes(item)}
                      onCheckedChange={() => {
                        const current = formData.cultivo_data.urocultivo?.sintomas || [];
                        const newValues = current.includes(item)
                          ? current.filter((v) => v !== item)
                          : [...current, item];
                        handleCultivoChange('urocultivo', {
                          ...formData.cultivo_data.urocultivo,
                          sintomas: newValues
                        });
                      }}
                    />
                    <span className="text-sm text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Cultivo Hongos */}
          <AccordionItem value="hongos">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-heading font-medium text-slate-900">
                Cultivo-Microscopía Búsqueda de Hongos
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 space-y-4">
              <div className="space-y-3 pt-2">
                <Label className="font-medium text-slate-700">
                  Tipo de lesión observada
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 checkbox-group">
                  {['Descamativa', 'Exudativa'].map((item) => (
                    <label key={item} className="flex items-center gap-2">
                      <Checkbox
                        data-testid={`hongos-lesion-${item.toLowerCase()}`}
                        checked={(formData.cultivo_data.hongos_tipo_lesion || []).includes(item)}
                        onCheckedChange={() => toggleCheckbox('hongos_tipo_lesion', item)}
                      />
                      <span className="text-sm text-slate-700">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hongos_sitio" className="font-medium text-slate-700">
                  Sitio de lesión
                </Label>
                <Input
                  id="hongos_sitio"
                  data-testid="hongos-sitio-input"
                  placeholder="Especifique el sitio de la lesión..."
                  value={formData.cultivo_data.hongos_sitio || ''}
                  onChange={(e) => handleCultivoChange('hongos_sitio', e.target.value)}
                  className="h-11"
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Coprocultivo */}
          <AccordionItem value="coprocultivo">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-heading font-medium text-slate-900">
                Coprocultivo
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 checkbox-group pt-2">
                {['Diarrea crónica', 'Diarrea aguda/reciente', 'Signos compatibles con dolor abdominal', 'Vómito', 'Letargia/decaimiento', 'Fiebre documentada (MVZ)'].map((item) => (
                  <label key={item} className="flex items-center gap-2">
                    <Checkbox
                      data-testid={`coprocultivo-${item.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}`}
                      checked={(formData.cultivo_data.coprocultivo || []).includes(item)}
                      onCheckedChange={() => toggleCheckbox('coprocultivo', item)}
                    />
                    <span className="text-sm text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-2">
                <Label htmlFor="coprocultivo_ingesta" className="font-medium text-slate-700">
                  Ingesta de alimento/líquido sospechoso ¿Cuál?
                </Label>
                <Input
                  id="coprocultivo_ingesta"
                  data-testid="coprocultivo-ingesta-input"
                  placeholder="Especifique..."
                  value={formData.cultivo_data.coprocultivo_ingesta || ''}
                  onChange={(e) =>
                    handleCultivoChange('coprocultivo_ingesta', e.target.value)
                  }
                  className="h-11"
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Cultivo Traqueal/Bronquial */}
          <AccordionItem value="traqueal">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-heading font-medium text-slate-900">
                Cultivo-Microscopía Traqueal/Bronquial
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 checkbox-group pt-2">
                {[
                  'Tos crónica',
                  'Tos reciente',
                  'Dificultad respiratoria (disnea)',
                  'Respiración rápida (taquipnea)',
                  'Pérdida de peso',
                  'Fiebre documentada (MVZ)'
                ].map((item) => (
                  <label key={item} className="flex items-center gap-2">
                    <Checkbox
                      data-testid={`traqueal-${item.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}`}
                      checked={(formData.cultivo_data.traqueal || []).includes(item)}
                      onCheckedChange={() => toggleCheckbox('traqueal', item)}
                    />
                    <span className="text-sm text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}